import { throwError as observableThrowError, Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { HttpHeaders } from '@angular/common/http';
import { Subscription } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  CommonCustomerItem,
  CommonFileItem,
  CommonSupplierItem,
  CurrentUser,
  TreeExportStatus,
  Chain,
} from '../../_models/contractManagement/common';
import { StoreModelService } from '../storeModel.service';
import { SM } from '../../../../assets/data/enum/storeModel.enum';
import { FarmService } from '../farm.service';

@Injectable()
export class CommonContractDataService {
  private subscriptions = new Subscription();

  private apiContractsUrl = environment.apiContractsUrl;
  private apiBaseUrl = environment.apiBaseUrl;
  private headers = new HttpHeaders();
  private customers: CommonCustomerItem[];
  private suppliers: CommonSupplierItem[];
  // Needed to intgrate current user data given from the analytics platform with some data needed by the contract management
  private currentUser: CurrentUser;
  private readOnlyUserContracts = false;
  private readOnlyUserAudit = false;
  private productCountries: any[] = [];

  constructor(
    private http: HttpClient,
    private farmService: FarmService,
    private storeModelService: StoreModelService
  ) {
    // everytime the farm changes or gets updated, also the headers should be re-valorized
    // Subscribe to selectedFarmGroup$
    this.subscriptions.add(
      this.farmService.selectedFarmGroup$.subscribe((g) => {
        if (g) {
          // Update headers with chainKey and role
          this.headers = this.headers
            .set('chainKey', g.group)
            .set('role', g.rolesInGroup[0]?.authority || '');
        } else {
          // Handle the case where g is null or undefined (e.g., logout or redirect)
          console.warn('No group selected. Consider redirecting or logging out.');
          // Example action: this.authService.logout();
          // Example action: this.router.navigate(['/login']);
        }
      })
    );

    this.storeModelService.list.subscribe((list) => {
      this.readOnlyUserContracts =
        !this.storeModelService.enableActionByStoreModelAndPermission(SM.SM_CONTRACT_MANAGEMENT)
          .create ||
        !this.storeModelService.enableActionByStoreModelAndPermission(SM.SM_CONTRACT_MANAGEMENT)
          .update;
      this.readOnlyUserAudit =
        !this.storeModelService.enableActionByStoreModelAndPermission(SM.SM_CONTRACT_AUDIT)
          .create ||
        !this.storeModelService.enableActionByStoreModelAndPermission(SM.SM_CONTRACT_AUDIT).update;
    });
  }

  // Gets the common customers list
  getCustomers(): CommonCustomerItem[] {
    return this.customers;
  }

  //Gets the common suppliers list
  getSuppliers(): CommonSupplierItem[] {
    return this.suppliers;
  }

  // Gets the current User
  getCurrentUser(): CurrentUser {
    return this.currentUser;
  }

  getProductAvailableCountries() {
    return this.productCountries;
  }

  // returns if the user is readOnly for contracts section
  userReadOnlyContracts(): boolean {
    return this.readOnlyUserContracts;
  }

  // returns if the user is readOnly for audits section
  userReadOnlyAudit(): boolean {
    return this.readOnlyUserAudit;
  }

  getDeltaCssClass(availableAmount: number, requiredAmount: number) {
    const deltaPercentage = ((availableAmount - requiredAmount) / requiredAmount) * 100;

    if (isNaN(deltaPercentage)) {
      return '';
    }

    if (deltaPercentage < 0) {
      return 'bg-danger';
    } else if (deltaPercentage >= 0) {
      return 'bg-success';
    } else {
      return 'bg-success';
    }
  }

  // get the delta for the contract line
  getDelta(availableAmount: number, requiredAmount: number) {
    return isNaN(availableAmount - requiredAmount) ? '-' : availableAmount - requiredAmount;
  }

  // Returns '+' sign if number is positive
  getSign(number) {
    return Math.sign(number) > 0 ? '+' : '';
  }

  // Stores the common customers list
  getAndStoreCustomers(): Observable<CommonCustomerItem[]> {
    return this.http
      .get<CommonCustomerItem[]>(`${this.apiContractsUrl}/common/client`, { headers: this.headers })
      .pipe(
        map((response) => {
          this.customers = response;
          return response;
        }),
        catchError((err: any) => {
          return throwError(err);
        })
      );
  }

  // Stores the common suppliers list
  getAndStoreSuppliers(): Observable<CommonSupplierItem[]> {
    return this.http
      .get<
        CommonSupplierItem[]
      >(`${this.apiContractsUrl}/common/supplier`, { headers: this.headers })
      .pipe(
        map((response) => {
          this.suppliers = response;
          return response;
        }),
        catchError((err: any) => {
          return observableThrowError(err);
        })
      );
  }

  // Gets the common customers list with a cropYear option
  getCustomersWithCropYear(cropYear: number): Observable<CommonCustomerItem[]> {
    return this.http
      .get<CommonCustomerItem[]>(`${this.apiContractsUrl}/common/client`, {
        headers: this.headers,
        params: { cropYear },
      })
      .pipe(
        catchError((err: any) => {
          return throwError(() => err);
        })
      );
  }

  // Gets the common suppliers list with a cropYear option
  getSuppliersWithCropYear(cropYear: number): Observable<CommonSupplierItem[]> {
    return this.http
      .get<CommonSupplierItem[]>(`${this.apiContractsUrl}/common/supplier`, {
        headers: this.headers,
        params: { cropYear },
      })
      .pipe(
        catchError((err: any) => {
          return throwError(() => err);
        })
      );
  }

  // Stores the common user data
  getAndStoreCurrentUser(): Observable<CurrentUser> {
    return this.http
      .get<CurrentUser>(`${this.apiContractsUrl}/common/me`, { headers: this.headers })
      .pipe(
        map((response) => {
          // Assegna l'utente corrente e aggiorna il nome
          this.currentUser = response;
          this.currentUser.name = this.farmService.getLocalCurrentFarm().name;
          return response;
        }),
        catchError((err: any) => {
          return throwError(() => err);
        })
      );
  }

  // Gets the current user data
  getMe(): Observable<CommonSupplierItem> {
    return this.http
      .get<CommonSupplierItem>(`${this.apiContractsUrl}/common/me`, { headers: this.headers })
      .pipe(
        catchError((err: any) => {
          return throwError(() => err);
        })
      );
  }

  // Downloads the tree export file
  downloadTreeExport(): Observable<Blob> {
    return this.http
      .get(`${this.apiContractsUrl}/report/tree?year=2023`, {
        headers: this.headers,
        responseType: 'blob',
      })
      .pipe(
        catchError((err: any) => {
          return throwError(() => err);
        })
      );
  }

  // Downloads the tree suppliers file
  downloadSupplierExport(): Observable<Blob> {
    return this.http
      .get(`${this.apiContractsUrl}/report/supplier?type=xlsx`, {
        headers: this.headers,
        responseType: 'blob',
      })
      .pipe(
        catchError((err: any) => {
          return throwError(() => err);
        })
      );
  }

  // Downloads the contracts export file
  downloadContractsExport(year: number): Observable<Blob> {
    return this.http
      .get(`${this.apiContractsUrl}/report/supplier/contracts?type=xlsx&year=${year}`, {
        headers: this.headers,
        responseType: 'blob',
      })
      .pipe(
        catchError((err: any) => {
          return throwError(() => err);
        })
      );
  }

  // Gets the countries list the same of politca agricola
  getAndStoreProductCountries() {
    return this.http.get<any>(`${this.apiBaseUrl}api/private/v1/lists/productCountries`).pipe(
      map((response) => {
        if (response) {
          this.productCountries = response;
          return response;
        }
      })
    );
  }

  // Download file utility, by passing a blob and a
  // common file item triggers a browser file download
  downloadFileTrigger(blob: any, file: CommonFileItem) {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    document.body.appendChild(a);
    a.setAttribute('style', 'display: none');
    a.href = url;
    a.download = `${file.name}${file.extension ? '.' + file.extension : ''}`;
    a.click();
    window.URL.revokeObjectURL(url);
    a.remove();
  }

  // Gets the tree export status
  getExportStatus(year: number): Observable<TreeExportStatus> {
    return this.http
      .get<TreeExportStatus>(`${this.apiContractsUrl}/report/treeStatus?year=${year}`, {
        headers: this.headers,
      })
      .pipe(
        map((response) => response),
        catchError((err: any) => {
          return observableThrowError(err);
        })
      );
  }

  // Gets export file name
  getLatestReport(year: number, withToken: boolean = false): Observable<TreeExportStatus> {
    return this.http
      .get<TreeExportStatus>(
        `${this.apiContractsUrl}/report/latestTreeReport?withToken=${withToken}&year=${year}`,
        { headers: this.headers }
      )
      .pipe(
        map((response) => response),
        catchError((err: any) => {
          return observableThrowError(err);
        })
      );
  }

  // Starts a new tree export
  startExport(year: number): Observable<TreeExportStatus> {
    return this.http
      .get<any>(`${this.apiContractsUrl}/report/tree?year=${year}`, { headers: this.headers })
      .pipe(
        map((response) => response),
        catchError((err: any) => {
          return observableThrowError(err);
        })
      );
  }

  getSupplyChains(): Observable<Chain[]> {
    return this.http
      .get<Chain[]>(`${this.apiContractsUrl}/v1/private/chain`, { headers: this.headers })
      .pipe(
        map((response) => response),
        catchError((err: any) => {
          return throwError(() => err);
        })
      );
  }
}
